import React from "react";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  FormHelperText,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { get } from "lodash";
import { getTransactionTypeOptions } from "../../api/codelist/codeListsSlice";

const PaymentMeansMultiSelect = ({
  id,
  name,
  label = "Transaction Type",
  control,
  errors,
  readOnly = false,
  onChange,
}) => {
  const transactionTypeOptions = useSelector(getTransactionTypeOptions);

  const codeStringToSelected = (codeString) => {
    if (
      !codeString ||
      typeof codeString !== "string" ||
      codeString.length !== 20
    ) {
      return [];
    }

    const selectedNames = [];
    for (let i = 0; i < 20; i++) {
      if (codeString[i] === "1") {
        const position = i + 1;
        const option = transactionTypeOptions.find(
          (opt) => opt.position === position,
        );
        if (option) {
          selectedNames.push(option.name);
        }
      }
    }
    return selectedNames;
  };

  const selectedToCodeString = (selectedNames) => {
    const codeArray = ['0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0', '0'];

    selectedNames.forEach((name) => {
      const option = transactionTypeOptions.find((opt) => opt.name === name);
      if (option && option.position >= 1 && option.position <= 20) {
        codeArray[option.position - 1] = "1";
      }
    });

    return codeArray.join("");
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const codeString =
          typeof field.value === "string" && field.value.length === 20
            ? field.value
            : "00000000000000000000";
        const selectedNames = codeStringToSelected(codeString);

        const errorMessage = get(errors, name, {})?.message;

        const handleDelete = (valueToDelete, event) => {
          if (event) {
            event.stopPropagation();
            event.preventDefault();
          }

          const newSelectedNames = selectedNames.filter(
            (name) => name !== valueToDelete,
          );
          const newCodeString = selectedToCodeString(newSelectedNames);
          field.onChange(newCodeString);
          if (onChange) onChange(newCodeString);
        };

        const handleChange = (e) => {
          const newSelectedNames =
            typeof e.target.value === "string"
              ? e.target.value.split(",")
              : e.target.value;
          const newCodeString = selectedToCodeString(newSelectedNames);
          field.onChange(newCodeString);
          if (onChange) onChange(newCodeString);
        };

        return (
          <FormControl
            fullWidth
            error={!!errorMessage}
            variant="outlined"
            size="small"
          >
            <InputLabel
              id={`${id || name}-label`}
              shrink={selectedNames.length > 0}
            >
              {label}
            </InputLabel>

            <Select
              labelId={`${id || name}-label`}
              id={id || name}
              multiple
              value={selectedNames}
              onChange={handleChange}
              input={
                <OutlinedInput
                  label={label}
                  notched={selectedNames.length > 0}
                />
              }
              renderValue={(selected) => {
                const safeSelected = Array.isArray(selected) ? selected : [];
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {safeSelected.map((value) => {
                      const option = transactionTypeOptions.find(
                        (opt) => opt.name === value,
                      );
                      return (
                        <Chip
                          disabled={readOnly}
                          key={value}
                          label={option ? option.name : value}
                          onDelete={(event) => handleDelete(value, event)}
                          deleteIcon={
                            <CancelIcon
                              onMouseDown={(event) => event.stopPropagation()}
                            />
                          }
                          size="small"
                        />
                      );
                    })}
                  </Box>
                );
              }}
              readOnly={readOnly}
            >
              {transactionTypeOptions.map((opt) => (
                <MenuItem key={opt.name} value={opt.name}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
            {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};

export default PaymentMeansMultiSelect;
